import { AgGridReact } from 'ag-grid-react';
import { useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import type { RootState } from '../../../../state/store';
import { type ColDef, type ColTypeDef, AllEnterpriseModule } from 'ag-grid-enterprise';
import { AgChartsEnterpriseModule } from 'ag-charts-enterprise';
import { type IAggFuncParams, ModuleRegistry, themeQuartz } from 'ag-grid-community';
import { useExtAConfigs } from '../../hooks/useExtAConfigs';
import { useParams } from 'react-router-dom';
import { fetchData } from '../../../../helpers/offthreadFetcher';
import { configureUrlWithParams } from '../../../../helpers/utils';

// Define the type for filter params
interface IFilterParamsCallbackParams {
  success: (values: any[]) => void;
  error: () => void;
  colDef: ColDef;
}

// Define the response type for filter values
interface FilterResponse {
  result: Array<{
    name: string;
  }>;
}

ModuleRegistry.registerModules([AllEnterpriseModule.with(AgChartsEnterpriseModule)]);

const PivotTable = () => {
  const theme = useSelector((state: RootState) => state.theme.mode);
  const { dashboard, widget } = useParams();
  const gridApiRef = useRef<any>(null);

  const {
    pivotTableRef,
    serverSideDataSource,
    reportConfigs,
    colDefs,
    toggleChartOption,
    chartOptionDisabled
  } = useExtAConfigs();
  const widgetUrl = reportConfigs.widgetUrl;
  const company = useSelector((state: RootState) => state.company);
  const filters = useSelector((state: RootState) => state.filters);

  // Year range
  const dateFilters = useSelector((state: RootState) => state.filters.dates);
  const [startYear, endYear] = dateFilters.map((date) => parseInt(date.split('-')[0]));

  // Function to fetch filter values from the server (declared early so it can be used below)
  const fetchFilterValues = useCallback(
    async (filterName: string, params: IFilterParamsCallbackParams) => {
      if (!dashboard || !widget || !widgetUrl || !filterName) {
        params.success([]);
        return;
      }

      try {
        const endpoint = configureUrlWithParams(
          `/api/v1/dar/${dashboard}/${widget}/${widgetUrl}/extended/dropdown-filters/${filterName}`,
          {
            ...filters,
            timezoneOffset: company?.company_details?.time_zone_offset,
            shouldUseEpocForTimeRange: company.shouldUseEpocForTimeRange
          }
        );
        const response = (await fetchData(endpoint, { method: 'GET' })) as FilterResponse;

        // Format and provide filter values to AG-Grid
        const filterValues = response.result.map((item) => item.name);
        params.success(filterValues);
      } catch (error) {
        console.error(`Error fetching filter values for ${filterName}:`, error);
        params.error();
      }
    },
    [dashboard, widget, widgetUrl, filters, company]
  );

  // Prepare column definitions with filter params and cell class rules for Excel export styling
  const enhancedColDefs = useMemo(() => {
    if (!colDefs || colDefs.length === 0 || !widgetUrl) return colDefs;

    return colDefs.map((col) => {
      // Skip columns without fields or that shouldn't be filtered
      if (!col.field || col.hide || ['rowGroup', 'pivot'].includes(col.colId || '')) {
        return col;
      }

      const baseCol = {
        ...col,
        // Add cell class rules for Excel export styling
        cellClassRules: {
          // Apply nonEmptyCell class to cells with non-null/non-empty values
          nonEmptyCell: (params: any) => {
            return (
              params.value !== null &&
              params.value !== undefined &&
              params.value !== '' &&
              params.value !== 0
            );
          }
        }
      };

      // Configure set filter for string columns
      if (col.type === 'string' || !col.type) {
        return {
          ...baseCol,
          filter: 'agSetColumnFilter',
          filterParams: {
            values: (params: IFilterParamsCallbackParams) => {
              fetchFilterValues(col.field || '', params);
            },
            buttons: ['apply', 'reset'],
            closeOnApply: true
          }
        };
      }

      return baseCol;
    });
  }, [colDefs, widgetUrl, fetchFilterValues]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      minWidth: 130,
      enableValue: true,
      enableRowGroup: true,
      enablePivot: true,
      resizable: true,
      lockPinned: true,
      suppressHeaderMenuButton: false,
      suppressHeaderContextMenu: false,
      allowedAggFuncs: ['sum', 'avg', 'count', 'distinctcount', 'min', 'max']
    };
  }, []);

  function getFilterYearLength(startYear: number, endYear: number) {
    const diff = endYear - startYear;
    const years: number[] = [startYear];
    if (diff > 0) {
      for (let i = startYear; i <= endYear; i++) {
        i += 1;
        years.push(i);
      }
    }
    return years;
  }

  // Define locale text with custom error message
  const localeText = useMemo(() => {
    return {
      loadingError: 'There was an error generating the report. Try again later.'
    };
  }, []);

  const colTypes = useMemo<{
    [key: string]: ColTypeDef;
  }>(() => {
    return {
      number: {
        // Measures use type "number"; they must not become row groups / pivots (matches Databricks extended widgets).
        enableRowGroup: false,
        enablePivot: false,
        filter: 'agNumberColumnFilter',
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) {
            return '';
          }
          // Format numbers with commas for thousands separators
          return params.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },
        filterParams: {
          buttons: ['apply', 'reset'],
          closeOnApply: true
        }
      },
      string: {
        enableValue: false,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: (params: IFilterParamsCallbackParams) => {
            const field = params.colDef.field;
            if (field) {
              fetchFilterValues(field, params);
            } else {
              params.success([]);
            }
          },
          buttons: ['apply', 'reset'],
          closeOnApply: true
        }
      },
      dateYear: {
        enableValue: false,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: getFilterYearLength(startYear, endYear),
          buttons: ['apply', 'reset'],
          closeOnApply: true
        }
      },
      dateMonth: {
        enableValue: false,
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['apply', 'reset'],
          closeOnApply: true
        }
      },
      dateDay: {
        enableValue: false,
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['apply', 'reset'],
          closeOnApply: true
        }
      }
    };
  }, [startYear, endYear, fetchFilterValues]);

  const aggFuncs = useMemo(() => {
    return {
      distinctcount: (params: IAggFuncParams<any, any>) => {
        return params.values;
      }
    };
  }, []);

  const reportTheme = useMemo(() => {
    return theme === 'dark'
      ? themeQuartz.withParams({
          accentColor: '#5D6A77',
          backgroundColor: '#2E2E2E',
          borderColor: '#03040526',
          borderRadius: 4,
          browserColorScheme: 'dark',
          cellTextColor: '#AEB9C0',
          columnBorder: true,
          fontSize: 15,
          foregroundColor: '#BEC7CD',
          headerBackgroundColor: '#18181A',
          headerFontSize: 14,
          headerTextColor: '#C6D3D8',
          oddRowBackgroundColor: '#6363630A',
          wrapperBorderRadius: 18
        })
      : themeQuartz.withParams({
          backgroundColor: '#FFFFFF',
          borderRadius: 4,
          browserColorScheme: 'inherit',
          fontSize: 15,
          headerFontSize: 14,
          headerTextColor: '#303030',
          wrapperBorderRadius: 18,
          columnDropCellBackgroundColor: '#d7d9db',
          columnDropCellTextColor: '#303030',
          columnDropCellBorder: '#6a6b6c',
          columnDropCellDragHandleColor: '#303030',
          borderColor: '#04050526',
          columnBorder: true,
          headerBackgroundColor: '#E9EBEC',
          oddRowBackgroundColor: '#FCFAFA'
        });
  }, [theme]);

  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);

  // Store grid API on ready
  const onGridReady = useCallback((params: any) => {
    gridApiRef.current = params.api;
  }, []);

  const toolPanels = useMemo(() => {
    const defaultPanels = [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressPivotMode: true,
          suppressPivots: true,
          suppressColumnFilter: true,
          suppressColumnSelectAll: true
        }
      }
    ];
    return [
      ...defaultPanels,
      {
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        toolPanel: 'agFiltersToolPanel'
      }
    ];
  }, []);

  function onColumnRowGroupChanged(event: any): void {
    const activeRowGroups = event.api.getRowGroupColumns().map((col: any) => col.getColDef().field);
    if (activeRowGroups.length && activeRowGroups.includes('business_date_time')) {
      toggleChartOption(true);
    } else {
      toggleChartOption(false);
    }
  }

  return (
    <Wrapper style={containerStyle}>
      <div style={gridStyle}>
        <AgGridReact
          ref={pivotTableRef}
          theme={reportTheme}
          columnDefs={enhancedColDefs}
          columnTypes={colTypes}
          defaultColDef={defaultColDef}
          gridOptions={{
            allowDragFromColumnsToolPanel: false
          }}
          localeText={localeText}
          enableCharts={true}
          cellSelection
          rowGroupPanelShow="always"
          groupDisplayType="groupRows"
          aggFuncs={aggFuncs}
          sideBar={{
            toolPanels
          }}
          grandTotalRow={'bottom'}
          pivotMode
          serverSideSortAllLevels
          rowModelType="serverSide"
          serverSideDatasource={serverSideDataSource}
          onGridReady={onGridReady}
        />
      </div>
    </Wrapper>
  );
};

export default PivotTable;

const Wrapper = styled.div`
  position: relative;

  .ag-column-drop-horizontal-list {
    overflow-x: scroll;
    /* Hide scrollbar while maintaining scroll functionality */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
    &::-webkit-scrollbar {
      width: 0;
      height: 0;
      background: transparent;
    }
  }
`;
